import { useEffect, useRef, useState } from "react";
import {
  parseSseChunk,
  type ParsedTurnStreamEvent,
  type PipelineVerboseStep,
} from "@/features/chat/lib/turnStream";
import type { ClarificationRequest } from "@/features/chat/model/chatTypes";
import { normalizeClarificationRequest } from "@/features/chat/model/chatTypes";
import { apiFetch } from "@/shared/api/apiFetch";

export type TurnStreamStatus =
  | "idle"
  | "connecting"
  | "streaming"
  | "waiting_for_clarification"
  | "complete"
  | "error"
  | "terminated";

export type UseTurnStreamOptions = {
  afterStepIndex?: number;
  initialVerboseLog?: PipelineVerboseStep[];
  initialStreamedText?: string;
  initialFollowUpQuestions?: string[];
  initialClarificationRequest?: ClarificationRequest | null;
  /** When true, replayed pause events do not close the SSE connection (post-clarification resume). */
  resumeAfterClarification?: boolean;
  /** Bumps stream reconnect when the turn id is unchanged but a new tail is needed. */
  reconnectKey?: number;
  onClarificationRequired?: (request: ClarificationRequest, turnId: string) => void;
};

type UseTurnStreamResult = {
  verboseLog: PipelineVerboseStep[];
  currentVerboseStep: PipelineVerboseStep | null;
  streamedText: string;
  followUpQuestions: string[];
  clarificationRequest: ClarificationRequest | null;
  hasReceivedToken: boolean;
  status: TurnStreamStatus;
  error: string | null;
  summary: string | null;
  insights: string[] | null;
  why: string | null;
  action: string | null;
  businessImpact: string | null;
};

function maxStepIndex(steps: PipelineVerboseStep[]): number {
  if (steps.length === 0) {
    return 0;
  }
  return Math.max(...steps.map((step) => step.stepIndex));
}

function parseClarificationRequest(raw: Record<string, unknown> | undefined): ClarificationRequest | null {
  return normalizeClarificationRequest(raw);
}

export function useTurnStream(
  turnId: string | null,
  options?: UseTurnStreamOptions | null,
): UseTurnStreamResult {
  const [verboseLog, setVerboseLog] = useState<PipelineVerboseStep[]>([]);
  const [currentVerboseStep, setCurrentVerboseStep] = useState<PipelineVerboseStep | null>(null);
  const [streamedText, setStreamedText] = useState("");
  const [followUpQuestions, setFollowUpQuestions] = useState<string[]>([]);
  const [clarificationRequest, setClarificationRequest] = useState<ClarificationRequest | null>(
    null,
  );
  const [hasReceivedToken, setHasReceivedToken] = useState(false);
  const [status, setStatus] = useState<TurnStreamStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [insights, setInsights] = useState<string[] | null>(null);
  const [why, setWhy] = useState<string | null>(null);
  const [action, setAction] = useState<string | null>(null);
  const [businessImpact, setBusinessImpact] = useState<string | null>(null);
  const activeTurnIdRef = useRef<string | null>(null);
  const appliedStepIndexRef = useRef(0);

  useEffect(() => {
    if (!turnId) {
      activeTurnIdRef.current = null;
      appliedStepIndexRef.current = 0;
      setVerboseLog([]);
      setCurrentVerboseStep(null);
      setStreamedText("");
      setFollowUpQuestions([]);
      setClarificationRequest(null);
      setHasReceivedToken(false);
      setStatus("idle");
      setError(null);
      setSummary(null);
      setInsights(null);
      setWhy(null);
      setAction(null);
      setBusinessImpact(null);
      return;
    }

    const abortController = new AbortController();
    activeTurnIdRef.current = turnId;

    const initialVerboseLog = options?.initialVerboseLog ?? [];
    const initialStreamedText = options?.initialStreamedText ?? "";
    const initialFollowUpQuestions = options?.initialFollowUpQuestions ?? [];
    const initialClarificationRequest = options?.initialClarificationRequest ?? null;
    const afterStepIndex =
      options?.afterStepIndex ?? (initialVerboseLog.length > 0 ? maxStepIndex(initialVerboseLog) : 0);

    appliedStepIndexRef.current = afterStepIndex;
    setVerboseLog(initialVerboseLog);
    setCurrentVerboseStep(
      initialVerboseLog.length > 0 ? initialVerboseLog[initialVerboseLog.length - 1] : null,
    );
    setStreamedText(initialStreamedText);
    setFollowUpQuestions(initialFollowUpQuestions);
    setClarificationRequest(
      options?.resumeAfterClarification ? null : initialClarificationRequest,
    );
    setHasReceivedToken(initialStreamedText.length > 0);
    setStatus("connecting");
    setError(null);

    const consumeStream = async () => {
      try {
        const query =
          afterStepIndex > 0 ? `?after_step_index=${encodeURIComponent(String(afterStepIndex))}` : "";
        const response = await apiFetch(`/v1/chat/turns/${turnId}/stream${query}`, {
          method: "GET",
          headers: { Accept: "text/event-stream" },
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Stream request failed with status ${response.status}.`);
        }

        if (!response.body) {
          throw new Error("Stream response did not include a body.");
        }

        if (activeTurnIdRef.current !== turnId) {
          return;
        }

        setStatus("streaming");

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let sawTerminalEvent = false;

        const handleEvent = (event: ParsedTurnStreamEvent): boolean => {
          if (event.type === "done") {
            sawTerminalEvent = true;
            if (event.summary) setSummary(event.summary);
            if (event.insights?.length) setInsights(event.insights);
            if (event.why) setWhy(event.why);
            if (event.action) setAction(event.action);
            if (event.business_impact) setBusinessImpact(event.business_impact);
            setStatus("complete");
            return true;
          }

          if (event.type === "terminated") {
            sawTerminalEvent = true;
            setStatus("terminated");
            return true;
          }

          if (event.type === "error") {
            sawTerminalEvent = true;
            setStatus("error");
            setError(event.message ?? event.content ?? "The assistant run failed.");
            return true;
          }

          if (event.type === "token" && event.delta) {
            setHasReceivedToken(true);
            setStreamedText((current) => current + event.delta);
            return false;
          }

          if (event.type === "follow_up_questions" && event.questions) {
            setFollowUpQuestions(event.questions);
            return false;
          }

          if (event.type === "clarification_required") {
            if (options?.resumeAfterClarification) {
              return false;
            }
            const request = parseClarificationRequest(event.request);
            if (request) {
              setClarificationRequest(request);
              options?.onClarificationRequired?.(request, turnId);
            }
            setStatus("waiting_for_clarification");
            return false;
          }

          if (event.type === "waiting_for_clarification") {
            if (options?.resumeAfterClarification) {
              return false;
            }
            const request = parseClarificationRequest(event.request);
            if (request) {
              setClarificationRequest(request);
              options?.onClarificationRequired?.(request, turnId);
            }
            sawTerminalEvent = true;
            setStatus("waiting_for_clarification");
            return true;
          }

          if (event.verboseStep) {
            const step = event.verboseStep as PipelineVerboseStep;
            if (step.stepIndex <= appliedStepIndexRef.current) {
              return false;
            }
            appliedStepIndexRef.current = step.stepIndex;
            setVerboseLog((current) => [...current, step]);
            setCurrentVerboseStep(step);
          }

          return false;
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const parsed = parseSseChunk(buffer);
          buffer = parsed.remainder;

          for (const event of parsed.events) {
            if (handleEvent(event)) {
              await reader.cancel();
              return;
            }
          }
        }

        if (activeTurnIdRef.current === turnId && sawTerminalEvent) {
          setStatus((current) =>
            current === "waiting_for_clarification" ? current : "complete",
          );
        }
      } catch (streamError) {
        if (abortController.signal.aborted) {
          return;
        }

        setStatus("error");
        setError(
          streamError instanceof Error
            ? streamError.message
            : "Unable to connect to the assistant stream.",
        );
      }
    };

    void consumeStream();

    return () => {
      abortController.abort();
      if (activeTurnIdRef.current === turnId) {
        activeTurnIdRef.current = null;
      }
    };
    // Individual option fields are listed so callers can pass a fresh options object each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- options identity is intentionally unstable
  }, [
    options?.afterStepIndex,
    options?.initialClarificationRequest,
    options?.initialFollowUpQuestions,
    options?.initialStreamedText,
    options?.initialVerboseLog,
    options?.onClarificationRequired,
    options?.reconnectKey,
    options?.resumeAfterClarification,
    turnId,
  ]);

  return {
    verboseLog,
    currentVerboseStep,
    streamedText,
    followUpQuestions,
    clarificationRequest,
    hasReceivedToken,
    status,
    error,
    summary,
    insights,
    why,
    action,
    businessImpact,
  };
}
